import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { setSearchParam } from 'helpers/setSearchParam';

import {
  isEmptyCollection,
  isUrlNamesMatch,
  SettingsManager,
  SiteConfigManager,
  useLabel,
  useSettings,
  useSportsbookState,
  useSportsbookStateUpdate,
  useSportState,
} from '@atlas/core';
import { Icon, Label, Loader } from '@atlas/ui';

import { useSimplifiedSportsbook } from 'common/SimplifiedSportsbook/SimplifiedSportsbookContext';
import { useTopTournamentsState } from 'components/Pages/Prematch/SimplifiedTopTournamentsProvider';
import { timeQueryName } from 'components/SimpleFilters/DropdownTime';
import { navigationTypes } from 'iframe/constants';

const topCategory = {
  id: 'top',
  name: 'all_top_leagues',
};

const allCategory = {
  id: 'all',
  name: 'all_leagues',
};

const defaultNavigationTypeCategories = {
  [navigationTypes.sportPrematch]: [topCategory],
  [navigationTypes.sportLive]: [allCategory, topCategory],
};

const TOP_CATEGORY_MAX_POSITION = 100;

const DropdownCategories = () => {
  const topTournaments = useTopTournamentsState();
  const { categories: sportCategories, initialized, loading } = useSportState();
  const sportsbookStateUpdate = useSportsbookStateUpdate();
  const { isLiveTopTournaments, setIsLiveTopTournaments } = useSimplifiedSportsbook();
  const { id, selectedCategoryUrlName, selectedSportUrlName } = useSportsbookState();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { resetFilter } = useSettings();
  const { search } = useLocation();
  const [activeCategory, setActiveCategory] = useState();
  const [categories, setCategories] = useState([]);
  const pageOptions =
    SiteConfigManager.getConfig('config.pages', []).find(
      ({ isActive, type }) => isActive && navigationTypes[type] === id,
    ).options || {};
  const categoryLabels = {
    [topCategory.id]: useLabel(topCategory.name),
    [allCategory.id]: useLabel(allCategory.name),
  };
  const alphabetLabel = useLabel('all_categories_a_z');

  useEffect(() => {
    let categories = sportCategories;

    if (pageOptions.topTournaments) {
      if (topTournaments && isEmptyCollection(topTournaments)) {
        if (id === navigationTypes.sportLive) {
          categories = [allCategory, ...sportCategories];
        } else {
          categories = [...sportCategories];
        }
      } else {
        categories = [...defaultNavigationTypeCategories[id], ...sportCategories];
      }
    }

    if (selectedCategoryUrlName && initialized && !loading) {
      const categoryExists = categories.some(({ urlName }) =>
        isUrlNamesMatch(selectedCategoryUrlName, urlName),
      );

      if (!categoryExists) {
        const toUrl = generatePath(path, { sportName: selectedSportUrlName });
        history.push(`${toUrl}${search}`);
      }
    }

    setCategories(categories);

    if (pageOptions.topTournaments) {
      if (
        !selectedCategoryUrlName &&
        activeCategory &&
        !categories.some(
          category => category.id === activeCategory || category.name === activeCategory,
        )
      ) {
        const category = categories[0];

        if (topCategory.id === category.id) {
          setIsLiveTopTournaments(true);
        } else if (isLiveTopTournaments) {
          setIsLiveTopTournaments(false);
        }

        changeCategory(category);
      }
    }
  }, [sportCategories, topTournaments]);

  useEffect(() => {
    if (
      categories.length > 0 &&
      !pageOptions.topTournaments &&
      !categories.find(({ urlName }) => isUrlNamesMatch(selectedCategoryUrlName, urlName))
    ) {
      changeCategory(categories[0]);
    }
  }, [categories, pageOptions, selectedCategoryUrlName]);

  useEffect(() => {
    let category;

    if (isLiveTopTournaments && categories.find(i => i.id === topCategory.id)) {
      category = topCategory;
    } else {
      const firstAvailableCategory = categories[0] || {};
      category =
        categories.find(({ urlName }) => isUrlNamesMatch(selectedCategoryUrlName, urlName)) ||
        firstAvailableCategory;
    }

    if (category.id) {
      setActiveCategory(category.id);

      if (!selectedCategoryUrlName) {
        const toUrl = generatePath(path, {
          categoryName: category.urlName,
          sportName: selectedSportUrlName,
        });

        history.push(`${toUrl}${search}`);
      }
    }
  }, [selectedCategoryUrlName, categories, isLiveTopTournaments]);

  const changeCategory = useCallback(
    category => {
      if (category?.id === activeCategory) {
        return;
      }

      const timeHours = new URLSearchParams(search).get(timeQueryName);
      const params =
        category?.id === topCategory.id || category?.id === allCategory?.id
          ? { sportName: selectedSportUrlName }
          : { categoryName: category?.urlName, sportName: category?.sportUrlName };

      const toUrl = generatePath(path, params);
      const filter = {
        dateFilterType: timeHours ? `${timeHours}h` : null,
        timeHours: timeHours || null,
      };

      SettingsManager.updateSettings({ resetFilter: filter });

      if (isEmptyCollection(resetFilter)) {
        sportsbookStateUpdate({
          categoryFilter: filter,
          tournamentFilter: filter,
        });
      }

      const newSearch = timeHours ? `?${setSearchParam(search, timeQueryName, timeHours)}` : search;
      history.push(`${toUrl}${newSearch}`);
    },
    [
      activeCategory,
      search,
      selectedSportUrlName,
      path,
      resetFilter,
      history,
      sportsbookStateUpdate,
    ],
  );

  const handleCategoryChange = useCallback(
    option => {
      const { value } = option.target;

      if (id === navigationTypes.sportLive && value === topCategory.id) {
        setIsLiveTopTournaments(true);
      } else if (isLiveTopTournaments) {
        setIsLiveTopTournaments(false);
      }

      const category = categories.find(({ id: categoryId }) => value === categoryId.toString());
      changeCategory(category);
    },
    [categories, changeCategory, isLiveTopTournaments],
  );

  const dropdownButtonText = useMemo(() => {
    const category = categories.find(({ id }) => id === activeCategory) || {};

    if (activeCategory === topCategory.id || activeCategory === allCategory.id) {
      return <Label className="button-text" message={category.name} />;
    }

    return category.name;
  }, [activeCategory, categories]);

  const renderOptions = React.useMemo(() => {
    let separatorSetFlag = false;

    return (
      <>
        {categories.map(({ id, name, position }, index) => {
          let separatorOption = null;

          if (!separatorSetFlag && position > TOP_CATEGORY_MAX_POSITION) {
            if (index !== 0) {
              separatorOption = (
                <>
                  <option disabled value="">
                    -----------------
                  </option>
                  <option disabled value="">
                    {alphabetLabel}
                  </option>
                </>
              );
            }
            separatorSetFlag = true;
          }

          return (
            <Fragment key={id}>
              {separatorOption}
              <option value={id}>
                {id === topCategory.id || id === allCategory.id ? categoryLabels[id] : name}
              </option>
            </Fragment>
          );
        })}
      </>
    );
  }, [categories]);

  if (!initialized && loading) {
    return (
      <div className="loading-container">
        <Loader className="absolute" />
      </div>
    );
  }

  return (
    <div className="dropdown bg select-field-container">
      <div className="dropdown-button">
        <div className="dropdown-button__text">{dropdownButtonText}</div>
        <Icon className="dropdown-button__icon-right" name="arrow-down" />
      </div>
      <h2 className="d-none">
        {categories.find(category => category.id === activeCategory)?.name}
      </h2>

      <select className="select-field" onChange={handleCategoryChange} value={activeCategory}>
        {renderOptions}
      </select>
    </div>
  );
};

export default DropdownCategories;
